import React, { useState, useEffect, useCallback, useRef } from "react";
import usePersistedState from '../../hooks/usePersistedState';
import useDebounce from '../../hooks/useDebounce';
import Paginate from '../../components/Paginate';
import { Link } from 'react-router-dom';
import { FaSortUp, FaSortDown } from 'react-icons/fa';
import useOfferService from '../../hooks/useOfferService';
import useClientService from '../../hooks/useClientService';
import useBuildingService from '../../hooks/useBuildingService';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Autocomplete,
    TextField,
    Button,
    Grid,
    CircularProgress,
    Typography
} from "@mui/material";
import { formatDate, itemsPerPage, formatAutocompleteOption, formatPrice, handleApiError } from '../../components/Utils';


function ListOffers() {
    const { getOffers } = useOfferService();
    const { getClients } = useClientService();
    const { getBuildings } = useBuildingService();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchReset, setFetchReset] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(null);

    const [offers, setOffers] = useState([]);

    const [name, setName] = usePersistedState('list_offers_name', null);
    const [status, setStatus] = useState(null);
    const [minBudget, setMinBudget] = usePersistedState('list_offers_minBudget', null);
    const [maxBudget, setMaxBudget] = usePersistedState('list_offers_maxBudget', null);
    const [code, setCode] = usePersistedState('list_offers_code', null);

    const debouncedName = useDebounce(name);
    const debouncedCode = useDebounce(code);
    const debouncedMinBudget = useDebounce(minBudget);
    const debouncedMaxBudget = useDebounce(maxBudget);

    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);

    const [buildings, setBuildings] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(null);

    const [sortColumn, setSortColumn] = usePersistedState('list_offers_sortColumn', '');
    const [sortDirection, setSortDirection] = usePersistedState('list_offers_sortDirection', 'ASC');

    const isFirstFetch = useRef(true);

    const fetchOffers = useCallback(async () => {
        try {
            if (isFirstFetch.current) setLoading(true);
            setError(null);
            const params = {};
            params.limit = itemsPerPage;
            params.offset = (currentPage - 1) * itemsPerPage;
            if (!fetchReset) {
                if (name) params.name = name;
                if (code) params.code = code;
                if (status) params.offer_status = status;
                if (minBudget) params.min_budget = minBudget;
                if (maxBudget) params.max_budget = maxBudget;
                if (sortColumn) params.sort_by = sortColumn;
                if (sortDirection) params.sort_direction = sortDirection;
                if (selectedClient) params.client_id = selectedClient.id;
                if (selectedBuilding) params.building_id = selectedBuilding.id;
            } else {
                setFetchReset(false);
            }
            const result = await getOffers(params);

            setOffers(result.data.items);
            setTotalItems(result.data.total);
            isFirstFetch.current = false;
        } catch (error) {
            setError(handleApiError(error, 'Error al cargar las ofertas.'));
        } finally {
            setLoading(false);
        }
    }, [currentPage, selectedClient, selectedBuilding, code, name, minBudget, maxBudget, status, sortColumn, sortDirection, fetchReset]);

    const fetchClients = useCallback(async () => {
        try {
            const result = await getClients();
            setClients(result.data.items);
        } catch (error) {
            setError(handleApiError(error, 'Error al cargar los clientes.'));
        }
    }, []);

    const fetchBuildings = useCallback(async () => {
        try {
            const result = await getBuildings();
            setBuildings(result.data.items);
        } catch (error) {
            setError(handleApiError(error, 'Error al cargar las obras.'));
        }
    }, []);

    useEffect(() => {
        fetchClients();
        fetchBuildings();
    }, []);

    useEffect(() => {
        fetchOffers();
    }, [currentPage, sortColumn, sortDirection, fetchReset, debouncedName, debouncedCode, debouncedMinBudget, debouncedMaxBudget, selectedClient, selectedBuilding, status]);

    const handleStatusChange = (event) => {
        const { value } = event.target;
        setStatus(value);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortDirection('ASC');
        }
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setCurrentPage(1);
        setName(null);
        setCode(null);
        setStatus(null);
        setMinBudget(null);
        setMaxBudget(null);
        setSelectedClient(null);
        setSelectedBuilding(null);
        setFetchReset(true);
    };

    const cellStyle = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '75px'
    };
    return (
        <div>
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading && (
                <>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Código"
                                        name="code"
                                        value={code || ""}
                                        onChange={(e) => setCode(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Nombre"
                                        name="name"
                                        value={name || ""}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </Grid>
                                <Grid item sm={2}>
                                    <Autocomplete
                                        options={clients}
                                        getOptionLabel={(option) => formatAutocompleteOption(option)}
                                        value={selectedClient}
                                        onChange={(event, newValue) => setSelectedClient(newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Cliente"
                                                style={{ maxWidth: '100%' }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item sm={2}>
                                    <Autocomplete
                                        options={buildings}
                                        getOptionLabel={(option) => formatAutocompleteOption(option)}
                                        value={selectedBuilding}
                                        onChange={(event, newValue) => setSelectedBuilding(newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Obra"
                                                style={{ maxWidth: '100%' }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Presupuesto min"
                                        name="minBudget"
                                        value={minBudget || ""}
                                        onChange={(e) => setMinBudget(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Presupuesto max"
                                        name="maxBudget"
                                        value={maxBudget || ""}
                                        onChange={(e) => setMaxBudget(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <FormControl fullWidth>
                                        <InputLabel id="status-label">Estado</InputLabel>
                                        <Select
                                            labelId="status-label"
                                            id="status"
                                            name="status"
                                            value={status}
                                            onChange={handleStatusChange}
                                            label="Tipo"
                                        >
                                            <MenuItem value="pending">PENDIENTE</MenuItem>
                                            <MenuItem value="accepted">ACEPTADA</MenuItem>
                                            <MenuItem value="rejected" >RECHAZADA</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container spacing={1} justifyContent="flex-end">
                                <Grid item>
                                    <Button variant="contained" color="secondary" onClick={handleResetFilters}>
                                        Resetear Filtros
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <br />
                    <br />
                    <br />
                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>
                                                Código {sortColumn === 'code' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                                                Nombre {sortColumn === 'name' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('budget')} style={{ cursor: 'pointer' }}>
                                                Presupuesto {sortColumn === 'budget' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('client_name')} style={{ cursor: 'pointer' }}>
                                                Cliente {sortColumn === 'client_name' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('building_name')} style={{ cursor: 'pointer' }}>
                                                Obra {sortColumn === 'building_name' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('start_date')} style={{ cursor: 'pointer' }}>
                                                Fecha inicio {sortColumn === 'start_date' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('end_date')} style={{ cursor: 'pointer' }}>
                                                Fecha final {sortColumn === 'end_date' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>Estado</Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {offers.map(item => (
                                    <TableRow key={item.id}>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            <Link to={`/offers/update/${item.id}`}>
                                                {item.code || '-'}
                                            </Link>
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {item.name || '-'}
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {formatPrice(item.budget)}
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {item.client && item.client.name ? (
                                                <Link to={`/clients/update/${item.client.id}`}>
                                                    {item.client.name}
                                                </Link>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {item.building && item.building.name ? (
                                                <Link to={`/buildings/update/${item.building.id}`}>
                                                    {item.building.name}
                                                </Link>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {formatDate(item.start_date) || '-'}
                                        </TableCell>
                                        <TableCell sx={cellStyle}>
                                            {formatDate(item.end_date) || '-'}
                                        </TableCell>
                                        <TableCell sx={cellStyle}>
                                            {item.offer_status === 'pending' ? 'PENDIENTE' :
                                                item.offer_status === 'accepted' ? 'ACEPTADA' :
                                                    item.offer_status === 'rejected' ? 'RECHAZADA' :
                                                        item.offer_status || '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                </>

            )}
            <br />
            <br />
            <br />
            <Paginate
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                currentPage={currentPage}
            />
            {error && (
                <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                    {error}
                </Typography>
            )}
        </div>
    )
}
export default ListOffers;
